/**
 * ChipButton - Small styled button (chip/pill) with background, border, and hitSlop.
 * Two variants: outline (transparent bg, colored border+text) and solid (colored bg, white text).
 */

import React from 'react';
import { TouchableOpacity, View, Text, type StyleProp, type ViewStyle } from 'react-native';
import { colors, FS_XS } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

const HIT_SLOP = { top: 8, bottom: 8, left: 8, right: 8 };

interface ChipButtonProps {
  label: string;
  onPress: () => void;
  testID: string;
  accessibilityLabel?: string;
  /** 'outline' = transparent bg + colored border/text; 'solid' = colored bg + white text */
  variant?: 'outline' | 'solid';
  /** Color for the chip (border+text for outline, bg for solid). Defaults to colors.primary */
  color?: string;
  /** Soft background color for outline variant. Defaults to theme.primarySoft */
  softColor?: string;
  disabled?: boolean;
  /** Extra style on the outer container */
  style?: StyleProp<ViewStyle>;
}

export const ChipButton = React.memo(function ChipButton({
  label,
  onPress,
  testID,
  accessibilityLabel,
  variant = 'outline',
  color: colorProp,
  softColor,
  disabled = false,
  style,
}: ChipButtonProps) {
  const { theme, fontScale, fontFamily } = useDisplayTheme();

  const chipColor = colorProp ?? colors.primary;
  const isOutline = variant === 'outline';

  const backgroundColor = isOutline
    ? (softColor ?? theme.primarySoft)
    : chipColor;
  const textColor = isOutline ? chipColor : '#FFFFFF';
  const borderColor = isOutline ? chipColor : 'transparent';

  return (
    <TouchableOpacity
      testID={testID}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.7}
      hitSlop={HIT_SLOP}
      style={style}
    >
      <View
        style={{
          backgroundColor,
          borderColor,
          borderWidth: 1,
          borderRadius: 8,
          paddingHorizontal: 12,
          paddingVertical: 6,
          alignSelf: 'flex-start',
        }}
      >
        <Text
          allowFontScaling={false}
          style={{
            fontSize: FS_XS * fontScale,
            fontWeight: '600',
            color: textColor,
            ...(fontFamily ? { fontFamily } : undefined),
          }}
        >
          {label}
        </Text>
      </View>
    </TouchableOpacity>
  );
});
